import AsyncStorage from "@react-native-async-storage/async-storage";
import { useEffect, useState } from "react";
import { useGlobal } from "@/Components/Context/globalContext";
import * as Location from "expo-location";
import Alert from "@/Components/Components/Alert";
import { getDistanceFromLatLonInMeters } from "@/Components/Functions/MapFunctions";
import { Platform } from "react-native";
import { DEMO, DEMO_LATITUDE, DEMO_LONGITUDE } from "@/config/constants";

const LOCATION_TIMEOUT_MS = 15000;

const useLocation = (itinerari: any, geofence: [any]) => {
  const { __, textRecurs, imatgeRecurs, projecte } = useGlobal();

  const DEFAULT_LATITUDE = projecte.latitud ?? 40;
  const DEFAULT_LONGITUDE = projecte.longitud ?? 3;

  const [isLoadingLocation, setIsLoadingLocation] = useState<boolean>(true);
  const [locationPermissionDenied, setLocationPermissionDenied] =
    useState<boolean>(false);

  const [currentLocation, setCurrentLocation] = useState<any>(
    DEMO ? { latitude: DEMO_LATITUDE, longitude: DEMO_LONGITUDE } : { latitude: null, longitude: null },
  );

  const [mapRegion, setMapRegion] = useState({
    latitude: DEMO ? DEMO_LATITUDE : DEFAULT_LATITUDE,
    longitude: DEMO ? DEMO_LONGITUDE : DEFAULT_LONGITUDE,
    latitudeDelta: 0.0922,
    longitudeDelta: 0.0421,
  });

  // Restore last known location as initial map region
  useEffect(() => {
    if (DEMO) return;
    AsyncStorage.getItem("lastLocation").then((saved) => {
      if (saved) {
        const { latitude, longitude } = JSON.parse(saved);
        setMapRegion((prev) =>
          prev.latitude === DEFAULT_LATITUDE && prev.longitude === DEFAULT_LONGITUDE
            ? { ...prev, latitude, longitude }
            : prev,
        );
      }
    });
  }, []);

  const [foraDelGeofence, setForaDelGeofence] = useState(false);

  const getCurrentPosition = async (): Promise<boolean> => {
    // Fast path: last known OS position (instant on iOS, avoids GPS cold start)
    try {
      const lastKnown = await Location.getLastKnownPositionAsync({ maxAge: 300_000 });
      if (lastKnown?.coords) {
        setCurrentLocation({ latitude: lastKnown.coords.latitude, longitude: lastKnown.coords.longitude });
        return true;
      }
    } catch (_) {}

    // Fresh position with timeout (Balanced is faster than Highest on iOS)
    try {
      const result = await Promise.race([
        Location.getCurrentPositionAsync({ accuracy: Location.Accuracy.Balanced }),
        new Promise<null>((resolve) => setTimeout(() => resolve(null), LOCATION_TIMEOUT_MS)),
      ]);
      if (!result) return false;
      setCurrentLocation({ latitude: result.coords.latitude, longitude: result.coords.longitude });
      return true;
    } catch (error) {
      console.log("getCurrentPosition error:", error);
      return false;
    }
  };

  const subscribeToLocationUpdates = () => {
    return Location.watchPositionAsync(
      {
        accuracy: Location.Accuracy.High,
        timeInterval: 5000,
        distanceInterval: 2,
      },
      (position) => {
        const { latitude, longitude } = position.coords;

        setCurrentLocation({
          latitude,
          longitude,
          latitudeDelta: 0.00622,
          longitudeDelta: 0.00421,
        });
      },
    ).catch((error) => {
      console.error("Error subscribing to location updates:", error);
    });
  };

  const getCurrentPositionWeb = (): Promise<{ lat: number; lon: number } | null> =>
    new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve(null);
        return;
      }
      const timeout = setTimeout(() => resolve(null), LOCATION_TIMEOUT_MS);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          clearTimeout(timeout);
          resolve({
            lat: position.coords.latitude,
            lon: position.coords.longitude,
          });
        },
        () => {
          clearTimeout(timeout);
          resolve(null);
        },
        { enableHighAccuracy: true, maximumAge: 10000, timeout: 10000 },
      );
    });

  const suscribeToLocationUpdatesWeb = () => {
    if (!navigator.geolocation) {
      return () => {};
    }

    const watchId = navigator.geolocation.watchPosition(
      (position) => {
        setCurrentLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
      },
      () => {
        // Don't block the user if tracking fails (e.g. incognito)
      },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 10000 },
    );

    return () => {
      navigator.geolocation.clearWatch(watchId);
    };
  };

  useEffect(() => {
    if (DEMO) {
      setIsLoadingLocation(false);
      return;
    }

    let subscription: { remove: () => void } | undefined;
    let cancelled = false;

    const stopLoadingWithError = () => {
      if (cancelled) return;
      setLocationPermissionDenied(true);
      setIsLoadingLocation(false);
    };

    const runLocationFlow = async () => {
      if (Platform.OS === "web") {
        const pos = await getCurrentPositionWeb();
        if (cancelled) return;
        if (pos) {
          setCurrentLocation({ latitude: pos.lat, longitude: pos.lon });
        }
        // Even without location (incognito), allow the user to continue with default coords
        suscribeToLocationUpdatesWeb();
        setIsLoadingLocation(false);
        return;
      }

      try {
        const { status: existingStatus } =
          await Location.getForegroundPermissionsAsync();
        if (cancelled) return;

        let status = existingStatus;
        if (existingStatus !== "granted") {
          const { status: requestedStatus } =
            await Location.requestForegroundPermissionsAsync();
          status = requestedStatus;
        }
        if (cancelled) return;

        if (status !== "granted") {
          Alert("", textRecurs("error_permission_location"));
          stopLoadingWithError();
          return;
        }

        const gotPosition = await getCurrentPosition();
        if (cancelled) return;
        if (!gotPosition) {
          Alert("", textRecurs("error_permission_location"));
          stopLoadingWithError();
          return;
        }

        const sub = await subscribeToLocationUpdates();
        if (cancelled) {
          (sub as { remove?: () => void })?.remove?.();
          return;
        }
        subscription = sub != null ? (sub as { remove: () => void }) : undefined;
      } catch (err) {
        console.error("Location permission/position error:", err);
        Alert("", textRecurs("error_permission_location"));
        stopLoadingWithError();
      }
    };

    runLocationFlow();

    return () => {
      cancelled = true;
      subscription?.remove();
    };
  }, []);

  useEffect(() => {
    if (
      !geofence.length ||
      currentLocation?.latitude == null ||
      currentLocation?.longitude == null
    ) {
      return;
    }

    const minDistance = geofence.reduce((minDist, currentCoord) => {
      const dist = getDistanceFromLatLonInMeters(
        currentLocation.latitude,
        currentLocation.longitude,
        currentCoord.latitude,
        currentCoord.longitude,
      );
      return Math.min(minDist, dist);
    }, Infinity);

    const threshold = itinerari.metres_activacio_geofence ?? 100;
    if (minDistance > threshold) {
      setForaDelGeofence(true);
    } else {
      setForaDelGeofence(false);
    }
  }, [currentLocation]);

  useEffect(() => {
    if (currentLocation.latitude) {
      AsyncStorage.setItem("lastLocation", JSON.stringify({
        latitude: currentLocation.latitude,
        longitude: currentLocation.longitude,
      }));

      if (mapRegion.latitude === DEFAULT_LATITUDE) {
        setMapRegion({
          ...currentLocation,
          latitudeDelta: 0.0922,
          longitudeDelta: 0.0421,
        });
      }

      setIsLoadingLocation(false);
    }
  }, [currentLocation]);

  return {
    currentLocation,
    mapRegion,
    foraDelGeofence,
    isLoadingLocation,
    locationPermissionDenied,
  };
};

export default useLocation;
